import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  DollarSign,
  TrendingUp,
  Activity,
  Edit,
  Save,
  X,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

const ClientDetails = ({ client, onUpdate, onClose }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    full_name: client?.full_name || '',
    email: client?.email || '',
    phone: client?.phone || '',
    address: client?.address || '',
    initial_capital: client?.initial_capital || 0,
    status: client?.status || 'active'
  });

  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateROI = (netWinnings, capital) => {
    if (capital === 0) return 0;
    return ((netWinnings / capital) * 100).toFixed(1);
  };

  const handleSave = () => {
    if (!editData.full_name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (!editData.email.trim()) {
      toast.error('El email es obligatorio');
      return;
    }
    if (editData.initial_capital < 0) {
      toast.error('El capital inicial no puede ser negativo');
      return;
    }

    if (onUpdate) {
      onUpdate(client.id, editData);
    }
    setIsEditing(false);
    toast.success('Cliente actualizado exitosamente');
  };

  const handleCancel = () => {
    setEditData({
      full_name: client?.full_name || '',
      email: client?.email || '',
      phone: client?.phone || '',
      address: client?.address || '',
      initial_capital: client?.initial_capital || 0,
      status: client?.status || 'active'
    });
    setIsEditing(false);
  };

  if (!client) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">
          No se encontró información del cliente
        </p>
      </div>
    );
  }

  const roi = calculateROI(client.net_winnings, client.initial_capital);
  const isProfit = Number(client.net_winnings) > 0;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-xl">
              {client.full_name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {client.full_name}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Cliente desde {formatDate(client.created_at)}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="btn-secondary"
            >
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={handleSave}
                className="btn-primary"
              >
                <Save className="w-4 h-4 mr-2" />
                Guardar
              </button>
              <button
                onClick={handleCancel}
                className="btn-secondary"
              >
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </button>
            </div>
          )}

          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Status Badge */}
      <div className="flex justify-start">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          client.status === 'active'
            ? 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
        }`}>
          <CheckCircle className="w-4 h-4 mr-1" />
          {client.status === 'active' ? 'Activo' : 'Inactivo'}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Información Personal */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-gradient-primary rounded-lg">
              <User className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Información Personal
            </h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nombre completo
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={editData.full_name}
                  onChange={(e) => setEditData({...editData, full_name: e.target.value})}
                  className="input-field"
                />
              ) : (
                <p className="text-gray-900 dark:text-gray-100">{client.full_name}</p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4 text-gray-400" />
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    value={editData.email}
                    onChange={(e) => setEditData({...editData, email: e.target.value})}
                    className="input-field"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{client.email}</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Phone className="w-4 h-4 text-gray-400" />
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Teléfono
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={editData.phone}
                    onChange={(e) => setEditData({...editData, phone: e.target.value})}
                    className="input-field"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{client.phone || 'No disponible'}</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Dirección
                </label>
                {isEditing ? (
                  <textarea
                    value={editData.address}
                    onChange={(e) => setEditData({...editData, address: e.target.value})}
                    className="input-field"
                    rows="2"
                  />
                ) : (
                  <p className="text-gray-900 dark:text-gray-100">{client.address || 'No disponible'}</p>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Información Financiera */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-gradient-to-r from-green-500 to-teal-600 rounded-lg">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Información Financiera
            </h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Capital inicial
              </label>
              {isEditing ? (
                <input
                  type="number"
                  value={editData.initial_capital}
                  onChange={(e) => setEditData({...editData, initial_capital: parseFloat(e.target.value) || 0})}
                  className="input-field"
                  min="0"
                  step="0.01"
                />
              ) : (
                <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {formatCurrency(client.initial_capital)}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Balance actual
                </label>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {formatCurrency(client.current_balance)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Total ganancias
                </label>
                <p className={`text-lg font-semibold ${
                  isProfit
                    ? 'text-success-600 dark:text-success-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {formatCurrency(client.total_winnings)}
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-dark-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  ROI
                </span>
                <div className="flex items-center space-x-2">
                  <TrendingUp className={`w-4 h-4 ${
                    roi > 0 ? 'text-success-600 dark:text-success-400' : 'text-gray-400'
                  }`} />
                  <span className={`font-bold ${
                    roi > 0
                      ? 'text-success-600 dark:text-success-400'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {roi}%
                  </span>
                </div>
              </div>

              <div className="w-full bg-gray-200 dark:bg-dark-700 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${
                    roi > 0 ? 'bg-gradient-success' : 'bg-gray-400 dark:bg-gray-600'
                  }`}
                  style={{
                    width: `${Math.min(Math.abs(roi), 100)}%`
                  }}
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Actividad */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-6 lg:col-span-2"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Resumen de Actividad
            </h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
              <Calendar className="w-6 h-6 text-primary-600 dark:text-primary-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-primary-700 dark:text-primary-300">
                {client.total_transactions || 0}
              </p>
              <p className="text-xs text-primary-600 dark:text-primary-400">
                Transacciones
              </p>
            </div>

            <div className="text-center p-4 bg-success-50 dark:bg-success-900/20 rounded-lg">
              <CheckCircle className="w-6 h-6 text-success-600 dark:text-success-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-success-700 dark:text-success-300">
                {client.active_bets || 0}
              </p>
              <p className="text-xs text-success-600 dark:text-success-400">
                Apuestas Activas
              </p>
            </div>

            <div className="text-center p-4 bg-warning-50 dark:bg-warning-900/20 rounded-lg">
              <TrendingUp className="w-6 h-6 text-warning-600 dark:text-warning-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-warning-700 dark:text-warning-300">
                {formatDate(client.last_transaction_date)}
              </p>
              <p className="text-xs text-warning-600 dark:text-warning-400">
                Última actividad
              </p>
            </div>

            <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <User className="w-6 h-6 text-gray-600 dark:text-gray-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                {formatDate(client.created_at)}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Fecha registro
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ClientDetails;